import {lazy, Suspense, useEffect, useLayoutEffect, type SyntheticEvent} from 'react'
import {IconLoader2} from '@tabler/icons-react'
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router'

import {HomeRoute} from '@/views/migration/HomeRoute'
import {LoginRoute} from '@/views/migration/LoginRoute'
import {ProfileRoute} from '@/views/migration/ProfileRoute'

const loadKincirLiveRoute = () => import('@/views/migration/KincirLiveRoute').then(({KincirLiveRoute}) => ({default: KincirLiveRoute}))
const loadKincirDownloadRoute = () => import('@/views/migration/KincirDownloadRoute')
const loadDaduRoute = () => import('@/views/migration/DaduRoute')
const loadPackageRoute = () => import('@/views/migration/PackageRoute')
const loadAuthenticateRoute = () => import('@/views/migration/AuthenticateRoute')
const loadHelpRoute = () => import('@/views/migration/HelpRoute')
const loadAboutRoute = () => import('@/views/migration/AboutRoute')
const loadNotFoundRoute = () => import('@/views/migration/NotFoundRoute')

const KincirLiveView = lazy(loadKincirLiveRoute)
const KincirDownloadView = lazy(loadKincirDownloadRoute)
const DaduView = lazy(loadDaduRoute)
const PackageView = lazy(loadPackageRoute)
const AuthenticateView = lazy(loadAuthenticateRoute)
const HelpView = lazy(loadHelpRoute)
const AboutView = lazy(loadAboutRoute)
const NotFoundView = lazy(loadNotFoundRoute)

const routePreloaders: Record<string, () => Promise<unknown>> = {
  '/kincir': loadKincirLiveRoute,
  '/kincir/download': loadKincirDownloadRoute,
  '/dadu': loadDaduRoute,
  '/pricing': loadPackageRoute,
  '/auth': loadAuthenticateRoute,
  '/help': loadHelpRoute,
  '/about': loadAboutRoute,
}

const preloadedRoutes = new Set<string>()

const scrollPositions = new Map<string, number>()

function ScrollManager() {
  const location = useLocation()
  const navigationType = useNavigationType()

  useLayoutEffect(() => {
    const currentKey = location.key
    const previousScrollRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'

    const restoreScroll = () => {
      window.scrollTo({
        top: navigationType === 'POP' ? (scrollPositions.get(currentKey) ?? 0) : 0,
        left: 0,
        behavior: 'instant',
      })
    }

    const frame = window.requestAnimationFrame(restoreScroll)

    return () => {
      window.cancelAnimationFrame(frame)
      scrollPositions.set(currentKey, window.scrollY)
      window.history.scrollRestoration = previousScrollRestoration
    }
  }, [location.key, navigationType])

  return null
}

function RouterFallback() {
  return <main className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground" role="status" aria-live="polite">
    <p className="inline-flex items-center gap-2 text-sm text-muted-foreground"><IconLoader2 aria-hidden="true" className="size-5 animate-spin" />Memuat halaman...</p>
  </main>
}

function preloadRoute(pathname: string) {
  const preloader = routePreloaders[pathname]
  if (preloader === undefined || preloadedRoutes.has(pathname)) return

  preloadedRoutes.add(pathname)
  void preloader().catch(() => preloadedRoutes.delete(pathname))
}

function preloadLinkedRoute(event: SyntheticEvent) {
  const target = event.target
  if (!(target instanceof Element)) return

  const link = target.closest('a[href]')
  if (!(link instanceof HTMLAnchorElement) || link.target || link.hasAttribute('download')) return

  const url = new URL(link.href, window.location.href)
  if (url.origin !== window.location.origin) return

  preloadRoute(url.pathname)
}

function PrimaryRoutePreloader() {
  const location = useLocation()

  useEffect(() => {
    if (location.pathname !== '/') return

    const preloadPrimaryRoutes = () => {
      preloadRoute('/kincir')
      preloadRoute('/dadu')
    }
    const idleCallback = window.requestIdleCallback

    if (idleCallback !== undefined) {
      const idleId = idleCallback(preloadPrimaryRoutes, {timeout: 2500})
      return () => window.cancelIdleCallback?.(idleId)
    }

    const timeoutId = window.setTimeout(preloadPrimaryRoutes, 0)
    return () => window.clearTimeout(timeoutId)
  }, [location.pathname])

  return null
}

function AppRoutes() {
  return (
    <div onPointerOverCapture={preloadLinkedRoute} onFocusCapture={preloadLinkedRoute} onTouchStartCapture={preloadLinkedRoute}>
      <ScrollManager />
      <PrimaryRoutePreloader />
      <Suspense fallback={<RouterFallback />}>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/kincir" element={<KincirLiveView />} />
          <Route path="/kincir/download" element={<KincirDownloadView />} />
          <Route path="/dadu" element={<DaduView />} />
          <Route path="/pricing" element={<PackageView />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/auth" element={<AuthenticateView />} />
          <Route path="/profile" element={<ProfileRoute />} />
          <Route path="/help" element={<HelpView />} />
          <Route path="/about" element={<AboutView />} />
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </Suspense>
    </div>
  )
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  )
}
